import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  query,
  where,
  getDocs,
  DocumentData,
} from '@angular/fire/firestore';
import { Observable, from, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class BatchService {
  constructor(private firestore: Firestore) {}

  /**
   * Query documents by array of IDs with automatic batching
   * @param collectionPath - Path to the Firestore collection
   * @param fieldName - Name of the field to query (e.g., 'id', 'userId')
   * @param values - Array of values to match (can be > 10 items)
   * @returns Observable of combined results
   */
  queryByArrayValues<T = DocumentData>(
    collectionPath: string,
    fieldName: string,
    values: string[]
  ): Observable<T[]> {
    if (!values || values.length === 0) {
      return of([]);
    }

    const CHUNK_SIZE = 10; // Firestore 'in' operator limit
    const chunks = this.chunkArray(values, CHUNK_SIZE);

    // Create array of observables for each chunk
    const queries$ = chunks.map((chunk) =>
      this.queryChunk<T>(collectionPath, fieldName, chunk)
    );

    // Execute all queries in parallel and combine results
    if (queries$.length === 0) {
      return of([]);
    }

    return forkJoin(queries$).pipe(
      map((results) => {
        // Flatten array of arrays and remove duplicates
        const combined = results.flat();
        return this.removeDuplicates(combined);
      })
    );
  }

  /**
   * Query a single chunk
   */
  private queryChunk<T>(
    collectionPath: string,
    fieldName: string,
    chunk: any[]
  ): Observable<T[]> {
    const colRef = collection(this.firestore, collectionPath);
    const q = query(colRef, where(fieldName, 'in', chunk));

    return from(getDocs(q)).pipe(
      map((snapshot) =>
        snapshot.docs.map(
          (doc) =>
            ({
              ...doc.data(),
              id: doc.id,
            } as T)
        )
      )
    );
  }

  /**
   * Split array into chunks
   */
  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  /**
   * Remove duplicate documents by id
   */
  private removeDuplicates<T>(items: T[]): T[] {
    const seen = new Set<string>();
    return items.filter((item) => {
      const id = (item as any).id;
      if (!id || seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
  }
}
